import { useEffect, useState, useCallback } from "react";
import {
  Truck, LogOut, Users, Clock, Activity, UserPlus, Download, Trash2, Calendar,
  ChevronRight, Loader2, FileText, Pencil, UserCog,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { fmtHours, weekMonday, weekRangeLabel, DAY_SHORT } from "@/lib/format";
import ShiftEditor from "@/components/ShiftEditor";
import DriverEditor from "@/components/DriverEditor";

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="ct-card p-4 fade-in" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
        <Icon className={`w-4 h-4 ${accent}`} /> {label}
      </div>
      <p className="font-display text-2xl font-extrabold">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [week, setWeek] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", truck_number: "", hourly_rate: "" });
  const [creating, setCreating] = useState(false);
  const [editDriver, setEditDriver] = useState(null);
  const [profileDriver, setProfileDriver] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/timesheets?week=${weekMonday(week)}`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [week]);

  useEffect(() => { load(); }, [load]);

  const createDriver = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/drivers", {
        ...form,
        hourly_rate: parseFloat(form.hourly_rate) || 0,
      });
      toast.success("Compte chauffeur créé");
      setForm({ name: "", email: "", password: "", truck_number: "", hourly_rate: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const removeDriver = async (id, name) => {
    if (!window.confirm(`Supprimer le compte de ${name} ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/drivers/${id}`);
      toast.success("Chauffeur supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportPdf = async (driverId) => {
    setExporting(true);
    try {
      const token = localStorage.getItem("ct_token");
      const url = `${API}/admin/export/pdf?week=${weekMonday(week)}${driverId ? `&driver_id=${driverId}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Échec de l'export");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `feuille_de_temps_${weekMonday(week)}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Rapport PDF généré");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen pb-16 relative z-10">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-[#0B0F17]/80 border-b border-[#1E293B]">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-[#F59E0B] flex items-center justify-center">
              <Truck className="w-5 h-5 text-[#0B0F17]" />
            </div>
            <div>
              <p className="font-display font-bold leading-none">Punch Transprestige <span className="text-[#F59E0B]">Admin</span></p>
              <p className="text-xs text-slate-400 mt-0.5">{user.email}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" data-testid="logout-button" onClick={logout} className="text-slate-400 hover:text-white">
            <LogOut className="w-4 h-4 mr-1" /> Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 pt-6 space-y-6">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Button size="icon" variant="outline" className="h-9 w-9 border-[#1E293B] bg-[#131C2E]" data-testid="week-prev-button" onClick={() => setWeek((w) => w - 1)}>
              <ChevronRight className="w-4 h-4 rotate-180" />
            </Button>
            <span className="text-sm font-medium flex items-center gap-1.5 min-w-[150px] justify-center">
              <Calendar className="w-4 h-4 text-[#F59E0B]" />
              {data ? weekRangeLabel(data.week_start) : "…"}
            </span>
            <Button size="icon" variant="outline" className="h-9 w-9 border-[#1E293B] bg-[#131C2E] disabled:opacity-30" disabled={week >= 0} data-testid="week-next-button" onClick={() => setWeek((w) => Math.min(0, w + 1))}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => exportPdf(null)}
              disabled={exporting}
              data-testid="admin-export-pdf-button"
              className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B0F17] font-semibold"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
              Export PDF (tous)
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-driver-button" className="border-[#1E293B] bg-[#131C2E] hover:bg-[#1E293B]">
                  <UserPlus className="w-4 h-4 mr-1" /> Nouveau chauffeur
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#131C2E] border-[#1E293B] text-white">
                <DialogHeader>
                  <DialogTitle className="font-display">Créer un compte chauffeur</DialogTitle>
                  <DialogDescription className="text-slate-400">
                    Le chauffeur pourra se connecter avec cet email et ce mot de passe.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={createDriver} className="space-y-3" data-testid="create-driver-form">
                  <div>
                    <Label className="text-slate-300">Nom complet</Label>
                    <Input required value={form.name} data-testid="driver-name-input" onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#1E293B]" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <Input required type="email" value={form.email} data-testid="driver-email-input" onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#1E293B]" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Mot de passe</Label>
                    <Input required value={form.password} data-testid="driver-password-input" onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#1E293B]" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-300">Camion #</Label>
                      <Input value={form.truck_number} data-testid="driver-truck-input" onChange={(e) => setForm({ ...form, truck_number: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#1E293B]" />
                    </div>
                    <div>
                      <Label className="text-slate-300">Taux horaire ($)</Label>
                      <Input type="number" step="0.01" value={form.hourly_rate} data-testid="driver-rate-input" onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="mt-1 bg-[#0B0F17] border-[#1E293B]" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={creating} data-testid="create-driver-submit" className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0B0F17] font-semibold w-full">
                      {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer le compte"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <StatCard icon={Users} label="Chauffeurs" value={data?.total_drivers ?? "—"} accent="text-[#F59E0B]" />
          <StatCard icon={Activity} label="En poste" value={data?.active_drivers ?? "—"} accent="text-emerald-400" />
          <StatCard icon={Clock} label="Total heures (sem.)" value={data ? fmtHours(data.grand_total_hours) : "—"} accent="text-sky-400" />
        </div>

        {/* Table */}
        <div className="ct-card overflow-hidden">
          <div className="p-4 border-b border-[#1E293B] flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#F59E0B]" />
            <h3 className="font-display font-bold">Feuilles de temps — tous les chauffeurs</h3>
          </div>

          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
          ) : data.drivers.length === 0 ? (
            <div className="py-16 text-center text-slate-500 text-sm">
              Aucun chauffeur. Créez le premier compte avec « Nouveau chauffeur ».
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-[#1E293B] hover:bg-transparent">
                    <TableHead className="text-slate-400">Chauffeur</TableHead>
                    {DAY_SHORT.map((d) => (
                      <TableHead key={d} className="text-slate-400 text-center px-2">{d}</TableHead>
                    ))}
                    <TableHead className="text-slate-400 text-right">Total</TableHead>
                    <TableHead className="text-slate-400 text-right">Suppl.</TableHead>
                    <TableHead className="text-slate-400 text-right">Prime</TableHead>
                    <TableHead className="text-slate-400 text-right">Paie</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.drivers.map((row) => (
                    <TableRow key={row.driver.id} className="border-[#1E293B]" data-testid={`driver-row-${row.driver.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${row.active ? "bg-emerald-400" : "bg-slate-600"}`} />
                          <div>
                            <p className="font-semibold">{row.driver.name}</p>
                            <p className="text-xs text-slate-500">
                              {row.driver.truck_number ? `Camion #${row.driver.truck_number}` : row.driver.email}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      {row.summary.days.map((day) => (
                        <TableCell key={day.date} className="text-center px-2 font-mono text-sm">
                          {day.hours > 0 ? day.hours.toFixed(1) : <span className="text-slate-600">—</span>}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-mono font-bold">{fmtHours(row.summary.total)}</TableCell>
                      <TableCell className="text-right font-mono text-[#F59E0B]">{fmtHours(row.summary.overtime)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-400" data-testid={`prime-cell-${row.driver.id}`}>
                        {row.prime > 0 ? `${row.prime.toFixed(2)}$` : <span className="text-slate-600">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold" data-testid={`pay-cell-${row.driver.id}`}>
                        {row.total_pay > 0 ? `${row.total_pay.toFixed(2)}$` : <span className="text-slate-600">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-emerald-400" data-testid={`profile-driver-${row.driver.id}`} onClick={() => setProfileDriver(row.driver)} title="Modifier le profil">
                            <UserCog className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-sky-400" data-testid={`edit-driver-${row.driver.id}`} onClick={() => setEditDriver(row.driver)} title="Corriger les heures / prime">
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-[#F59E0B]" data-testid={`export-driver-${row.driver.id}`} onClick={() => exportPdf(row.driver.id)} title="Export PDF de ce chauffeur">
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-400" data-testid={`delete-driver-${row.driver.id}`} onClick={() => removeDriver(row.driver.id, row.driver.name)} title="Supprimer">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </main>

      <ShiftEditor
        driver={editDriver}
        week={weekMonday(week)}
        open={!!editDriver}
        onOpenChange={(v) => !v && setEditDriver(null)}
        onChanged={load}
      />

      <DriverEditor
        driver={profileDriver}
        open={!!profileDriver}
        onOpenChange={(v) => !v && setProfileDriver(null)}
        onSaved={load}
      />
    </div>
  );
}
